import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useCookieConsent } from "@/context/CookieContext";

const categories = [
  {
    key: "necessary",
    title: "Strictly Necessary",
    description:
      "Required for core site security, form submission, session management and storing your consent preferences. Always enabled.",
    locked: true,
  },
  {
    key: "analytics",
    title: "Analytics",
    description:
      "Help us understand visitor behaviour with privacy-respecting analytics (e.g. Google Analytics, Plausible, Microsoft Clarity). Disabled until consent is granted.",
  },
  {
    key: "marketing",
    title: "Marketing",
    description:
      "Used for campaign attribution and audience building via tools such as LinkedIn Insight Tag. Disabled by default.",
  },
];

export default function CookiePreferences() {
  const { showPreferences, closePreferences, prefs, savePreferences } = useCookieConsent();
  const [draft, setDraft] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    if (showPreferences) {
      setDraft({ analytics: !!prefs.analytics, marketing: !!prefs.marketing });
    }
  }, [showPreferences, prefs]);

  if (!showPreferences) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cookie preferences"
      data-testid="cookie-preferences-modal"
      className="fixed inset-0 z-[60] flex items-end md:items-center justify-center bg-black/60"
      onClick={closePreferences}
    >
      <div
        className="bg-white text-[#0A1F44] w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[#0A1F44]/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 md:p-8 border-b border-[#0A1F44]/10">
          <div>
            <div className="evg-overline">Privacy Centre</div>
            <h2 className="font-serif text-3xl mt-2">Cookie Preferences</h2>
          </div>
          <button
            type="button"
            data-testid="cookie-preferences-close"
            onClick={closePreferences}
            aria-label="Close"
            className="p-1 text-[#0A1F44]/60 hover:text-[#0A1F44]"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 md:p-8 space-y-6">
          <p className="text-sm leading-relaxed text-[#0A1F44]/75">
            Choose which categories of cookies you would like to allow. You can update your
            preferences at any time via the Cookie Settings link in the footer.
          </p>

          {categories.map((cat) => {
            const checked = cat.locked ? true : !!draft[cat.key];
            return (
              <div
                key={cat.key}
                className="border border-[#0A1F44]/10 p-5 flex items-start gap-4"
                data-testid={`cookie-category-${cat.key}`}
              >
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-serif text-xl">{cat.title}</h3>
                    {cat.locked ? (
                      <span className="text-[10px] uppercase tracking-[0.2em] text-[#0A1F44]/55">
                        Always On
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-2 text-sm text-[#0A1F44]/70 leading-relaxed">{cat.description}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer mt-1">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={checked}
                    disabled={cat.locked}
                    data-testid={`cookie-toggle-${cat.key}`}
                    onChange={(e) =>
                      !cat.locked &&
                      setDraft((d) => ({ ...d, [cat.key]: e.target.checked }))
                    }
                  />
                  <span
                    className={`w-11 h-6 border transition-colors ${
                      checked
                        ? "bg-[#0A1F44] border-[#0A1F44]"
                        : "bg-white border-[#0A1F44]/30"
                    } ${cat.locked ? "opacity-70" : ""}`}
                  />
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 transition-transform ${
                      checked ? "translate-x-5 bg-[#D4AF37]" : "bg-[#0A1F44]/40"
                    }`}
                  />
                </label>
              </div>
            );
          })}
        </div>

        <div className="p-6 md:p-8 border-t border-[#0A1F44]/10 flex flex-wrap gap-3 justify-end">
          <button
            type="button"
            data-testid="cookie-preferences-reject"
            onClick={() => savePreferences({ analytics: false, marketing: false })}
            className="text-[11px] uppercase tracking-[0.2em] font-medium px-5 py-3 border border-[#0A1F44] text-[#0A1F44] hover:bg-[#0A1F44] hover:text-white transition-colors"
          >
            Reject Non-Essential
          </button>
          <button
            type="button"
            data-testid="cookie-preferences-save"
            onClick={() => savePreferences(draft)}
            className="text-[11px] uppercase tracking-[0.2em] font-medium px-5 py-3 bg-[#0A1F44] text-white border border-[#0A1F44] hover:bg-[#0A1F44]/90 transition-colors"
          >
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  );
}
